﻿// ── src/js/Fortuner.js ──
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GroundedSkybox } from 'three/examples/jsm/objects/GroundedSkybox.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { gsap } from 'gsap';

// 1) CAMERA POSITIONS
const defaultCameraPos = new THREE.Vector3(-20, 7, 20);
const defaultTargetPos = new THREE.Vector3(0, 2, 0);
const interiorCamPos = new THREE.Vector3(3.3, 7, -1.6);
const interiorTgtPos = new THREE.Vector3(-50, -4, 16.4);

// 2) ORBIT CONFIG
const orbitStart = defaultCameraPos.clone();
const orbitRadius = Math.hypot(orbitStart.x, orbitStart.z);
const orbitHeight = orbitStart.y;
const orbitSpeed = 0.004;
let rotating = false, rotAngle = 0;

// 3) SPIN CONFIG
let isWheelsSpinning = false;
let spinStartTime = 0;
const spinDuration = 2000, baseSpeed = 0.15;

// 4) THREE.JS CORE VARS
let camera, scene, renderer, controls;
let paintMesh, wheelRF, wheelLF, wheelRR, wheelLR;
let isInterior = false;

// 5) INIT
async function init() {
    // — camera & scene —
    camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 1, 1000);
    camera.position.copy(defaultCameraPos);
    scene = new THREE.Scene();

    // — renderer & shadows & clear color —
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setClearColor(0x333333);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    document.body.appendChild(renderer.domElement);

    // — lights —
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(10, 20, 10);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(2048, 2048);
    dirLight.shadow.camera.near = 1;
    dirLight.shadow.camera.far = 50;
    const d = 100;
    dirLight.shadow.camera.left = -d;
    dirLight.shadow.camera.right = d;
    dirLight.shadow.camera.top = d;
    dirLight.shadow.camera.bottom = -d;
    dirLight.shadow.camera.near = 1;
    dirLight.shadow.camera.far = 200;

    
    scene.add(dirLight);

    const amb = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(amb);

    // — ground to receive shadows —
    const ground = new THREE.Mesh(
        new THREE.PlaneGeometry(500, 500),
        new THREE.ShadowMaterial({ opacity: 0.4 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0.0;     // tweak under wheels
    ground.receiveShadow = true;
    scene.add(ground);

    // — controls & resize & key listeners —
    controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(defaultTargetPos);
    controls.maxPolarAngle = Math.PI / 2;
    controls.maxDistance = 80;
    controls.minDistance = 20;
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.zoomSpeed = 1;
    controls.addEventListener('change', render);
    controls.update();
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('keydown', onKeydown);

    // — environment & skybox —
    const hdr = await new RGBELoader().loadAsync('../assets/minedump_flats_4k.hdr');
    hdr.mapping = THREE.EquirectangularReflectionMapping;
    scene.environment = hdr;
    const sky = new GroundedSkybox(hdr, /*height*/15, /*radius*/100);
    sky.position.y = 15 - 0.01;
    scene.add(sky);

    // — load model & setup shadows/paint/wheels —
    const draco = new DRACOLoader().setDecoderPath('jsm/libs/draco/gltf/');
    const loader = new GLTFLoader().setDRACOLoader(draco);
    loader.load('../assets/scene.gltf', gltf => {
        gltf.scene.traverse(obj => {
            if (obj.isMesh) {
                obj.castShadow = true;
                obj.receiveShadow = true;
                obj.material.envMap = scene.environment;
                if ('metalness' in obj.material) {
                    obj.material.metalness = 0.9;
                    obj.material.roughness = 0;
                }
                if (obj.name === 'Object023_carpaint_0') {
                    paintMesh = obj;
                    paintMesh.material.color.set('#416c84');
                }
            }
        });

        wheelRF = gltf.scene.getObjectByName('WHEEL_RF');
        wheelLF = gltf.scene.getObjectByName('WHEEL_LF');
        wheelRR = gltf.scene.getObjectByName('WHEEL_RR');
        wheelLR = gltf.scene.getObjectByName('WHEEL_LR');

        gltf.scene.scale.set(5, 5, 5);
        gltf.scene.rotation.y = 5.1;
        scene.add(gltf.scene);

        render();
        setTimeout(buildUI, 0);
    });

    animate();
}

// 6) BUILD UI
function buildUI() {
    const colors = {
        '#ffffff': { metalness: 1, roughness: 0.15 },
        '#333333': { metalness: 0.8, roughness: 0.3 },
        '#416c84': { metalness: 0.4, roughness: 0.2 },
        '#ff0000': { metalness: 1, roughness: 0.3 },
        '#808080': { metalness: 0.9, roughness: 0.2 },
        '#2a3439': { metalness: 1, roughness: 0.15 },
        '#b87333': { metalness: 0.8, roughness: 0.3 },
        '#ff8c00': { metalness: 1, roughness: 0.1 }
    };

    const navBar = document.createElement('div');
    Object.assign(navBar.style, {
        position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)',
        width: '70%', padding: '10px 20px', borderRadius: '8px',
        backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)',
        display: 'flex', justifyContent: 'space-between', color: '#fff', zIndex: 999
    });
    document.body.appendChild(navBar);

    const brand = document.createElement('div'); brand.innerText = 'Fortuner';
    navBar.appendChild(brand);

    const container = document.createElement('div');
    container.style.display = 'flex'; container.style.gap = '20px';
    navBar.appendChild(container);

    let interiorBtn, infoBtn;
    ['COLOR', 'WHEELS', 'INTERIOR', 'INFO'].forEach(item => {
        const btn = document.createElement('div');
        btn.innerText = item;
        btn.style.cursor = 'pointer';
        btn.style.fontSize = '0.9rem';
        if (item === 'INTERIOR') interiorBtn = btn;
        if (item === 'INFO') infoBtn = btn;
        container.appendChild(btn);

        btn.onclick = () => {
            document.querySelector('#colorPanel')?.remove();
            document.querySelector('#infoPanel')?.remove();

            switch (item) {
                case 'COLOR': {
                    const panel = document.createElement('div');
                    panel.id = 'colorPanel';
                    Object.assign(panel.style, {
                        position: 'absolute', top: '70px', left: '50%',
                        transform: 'translateX(-50%)', padding: '10px',
                        borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(10px)', display: 'flex', gap: '8px',
                        color: '#fff', zIndex: 998
                    });
                    Object.entries(colors).forEach(([col, sett]) => {
                        const sw = document.createElement('div');
                        Object.assign(sw.style, {
                            width: '30px', height: '30px', borderRadius: '4px',
                            backgroundColor: col, border: '1px solid #ccc', cursor: 'pointer'
                        });
                        sw.onclick = () => {
                            if (paintMesh) {
                                paintMesh.material.color.set(col);
                                paintMesh.material.metalness = sett.metalness;
                                paintMesh.material.roughness = sett.roughness;
                                render();
                            }
                        };
                        panel.appendChild(sw);
                    });
                    document.body.appendChild(panel);
                    moveTo(defaultCameraPos, defaultTargetPos, () => {
                        controls.enableRotate = true;
                        controls.enableZoom = true;
                    });
                    break;
                }

                case 'WHEELS':
                    if (wheelRF && wheelLF && wheelRR && wheelLR) {
                        isWheelsSpinning = true;
                        spinStartTime = performance.now();
                    }
                    break;

                case 'INTERIOR':
                    if (!isInterior) {
                        moveTo(interiorCamPos, interiorTgtPos, () => {
                            controls.enableRotate = false;
                            controls.enableZoom = false;
                            interiorBtn.innerText = 'EXTERIOR';
                            isInterior = true;
                        });
                    } else {
                        moveTo(defaultCameraPos, defaultTargetPos, () => {
                            controls.enableRotate = true;
                            controls.enableZoom = true;
                            interiorBtn.innerText = 'INTERIOR';
                            isInterior = false;
                        });
                    }
                    break;

                case 'INFO': {
                    const info = document.createElement('div');
                    info.id = 'infoPanel';
                    Object.assign(info.style, {
                        position: 'absolute', padding: '15px', borderRadius: '8px',
                        backgroundColor: 'rgba(100,100,100,0.6)', color: '#fff', zIndex: 1001,
                        transformOrigin: 'top center', opacity: '0', transform: 'scale(0.8)',
                        transition: 'opacity 0.3s ease, transform 0.3s ease'
                    });
                    info.innerHTML = `
            <h3>Toyota Fortuner (2025)</h3>
            <strong>Engines:</strong>
            <ul>
              <li>2.7L Petrol – 166 PS</li>
              <li>2.8L Diesel – 204 PS, 500 Nm</li>
            </ul>
            <strong>Transmission:</strong> 6-speed manual/automatic<br/>
            <strong>Drive Type:</strong> 4x2 and 4x4<br/>
            <strong>Seating:</strong> 7-seater<br/>
            <strong>Features:</strong> 8" touchscreen w/ AA/AC, 7 airbags, Hill Assist, Stability Control<br/>
            <strong>Price:</strong> ₹33.43–₹51.44 lakh<br/>
            <div style="text-align:right; margin-top:10px;">
              <button id="closeInfo" style="cursor:pointer;">Close</button>
            </div>`;
                    const rect = infoBtn.getBoundingClientRect();
                    info.style.top = `${rect.bottom + 8}px`;
                    info.style.left = `${rect.left + rect.width / 2}px`;
                    info.style.transform = 'translateX(-50%) scale(0.8)';
                    document.body.appendChild(info);
                    requestAnimationFrame(() => {
                        info.style.opacity = '1';
                        info.style.transform = 'translateX(-50%) scale(1)';
                    });
                    document.getElementById('closeInfo').onclick = () => {
                        info.style.opacity = '0';
                        info.style.transform = 'translateX(-50%) scale(0.8)';
                        setTimeout(() => info.remove(), 300);
                    };
                    break;
                }
            }
        };
    });
}

// 7) ANIMATE LOOP
function animate() {
    requestAnimationFrame(animate);

    // spin wheels
    if (isWheelsSpinning) {
        const elapsed = performance.now() - spinStartTime;
        let speed = elapsed < 1500
            ? baseSpeed
            : elapsed < spinDuration
                ? baseSpeed * ((spinDuration - elapsed) / (spinDuration - 1500))
                : (isWheelsSpinning = false, 0);
        [wheelRF, wheelLF, wheelRR, wheelLR].forEach(w => w && (w.rotation.x += speed));
    }

    // camera orbit
    if (rotating) {
        rotAngle += orbitSpeed;
        camera.position.x = orbitRadius * Math.sin(rotAngle);
        camera.position.z = orbitRadius * Math.cos(rotAngle);
        camera.position.y = orbitHeight;
        camera.lookAt(0, 0, 0);
        if (rotAngle >= Math.atan2(orbitStart.x, orbitStart.z) + Math.PI * 2) {
            rotating = false;
        }
    }

    controls.update();
    renderer.render(scene, camera);
}

function moveTo(camPos, tgtPos, cb) {
    gsap.to(camera.position, {
        x: camPos.x, y: camPos.y, z: camPos.z,
        duration: 1, ease: 'power2.inOut',
        onUpdate: () => { controls.update(); render(); },
        onComplete: cb
    });
    gsap.to(controls.target, {
        x: tgtPos.x, y: tgtPos.y, z: tgtPos.z,
        duration: 1, ease: 'power2.inOut',
        onUpdate: () => { controls.update(); render(); }
    });
}


// 8) INPUT HANDLER
function onKeydown(e) {
    const key = e.key.toLowerCase();
    if (key === 'c' && !rotating) {
        rotating = true;
        rotAngle = Math.atan2(orbitStart.x, orbitStart.z);
    }
    if (key === 's' && !isWheelsSpinning) {
        isWheelsSpinning = true;
        spinStartTime = performance.now();
    }
}

// 9) RESIZE & RENDER
function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    render();
}
function render() {
    renderer.render(scene, camera);
}

// 10) EXPORT INIT
export async function initFortuner(container = document.body) {
    try { await init(); }
    catch (err) { console.error('Fortuner error:', err); }
}
